import LegalPage from "@/components/LegalPage";

export default function Privacy() {
  return (
    <LegalPage title="Privacy Policy" lastUpdated="17 September 2026">
      <p>
        This Privacy Policy explains how <strong>VoiceMatrixAI</strong> ("we", "us",
        "our") collects, uses, stores, and protects your personal information when you
        use <strong>voicematrix.in</strong> and its related services (the "Service").
        By using the Service, you consent to the practices described here.
      </p>

      <h2>1. Information We Collect</h2>
      <h3>1.1 Information you provide</h3>
      <ul>
        <li>Name and email address (at registration)</li>
        <li>Phone number (when you provide it at checkout or booking)</li>
        <li>Voice recordings you submit for analysis</li>
        <li>Payment information (processed by our payment partners — we do not store card or UPI credentials)</li>
        <li>Communications with our team (email, contact form, support requests)</li>
      </ul>
      <h3>1.2 Information collected automatically</h3>
      <ul>
        <li>Device type, browser, operating system</li>
        <li>IP address and approximate location</li>
        <li>Pages visited, features used, time spent</li>
        <li>Referring URLs and interactions with emails</li>
      </ul>
      <h3>1.3 Voice and audio data</h3>
      <p>
        When you record a voice sample, we capture the audio and extract acoustic
        features (pitch, frequency, note distribution). These features are used to
        generate your health and astro reports. The original recording and extracted
        features are stored in our secured systems for the period necessary to
        generate your report and maintain your scan history.
      </p>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>To provide the Service — generate your voice-frequency reports, astrological analyses, remedy recommendations, and PDF outputs.</li>
        <li>To operate your account — authentication, scan history, purchase records.</li>
        <li>To process payments — via Stripe and Razorpay. We receive confirmation of payment but not your full card details.</li>
        <li>To communicate — transactional emails, service updates, and (with consent) marketing emails.</li>
        <li>To improve the Service — aggregate, de-identified usage analysis.</li>
      </ul>

      <h2>3. AI Processing and Automated Analysis</h2>
      <p>
        We use artificial intelligence and machine learning systems to analyse voice
        samples, generate insights, and produce reports. Specifically:
      </p>
      <ul>
        <li>Voice and text analysis is performed using third-party large language and audio models. Your data is transmitted to these providers solely for the purpose of generating your result.</li>
        <li>We do not permit our AI providers to use your voice recordings or personal data to train their foundation models.</li>
        <li>AI outputs are assistive, not diagnostic. Reports are generated for wellness reflection and are not a substitute for medical or professional advice.</li>
        <li>We retain a record of AI-generated outputs associated with your account so you can revisit past reports.</li>
      </ul>
      <p>
        If you do not wish your data to be processed by AI systems, do not submit voice
        samples. There is no alternative delivery method.
      </p>

      <h2>4. Data Sharing</h2>
      <p>We do not sell your personal information. We share data only with:</p>
      <ul>
        <li>Service providers who help us operate the Service: hosting (Render, Cloudflare), database (MongoDB Atlas), payment processing (Stripe, Razorpay), email delivery, file storage (Google Drive), and AI model providers.</li>
        <li>Legal authorities when required by law, court order, or to protect the safety of users or the public.</li>
        <li>Successors in the event of a merger, acquisition, or sale of assets, with notice to you.</li>
      </ul>
      <p>
        All third-party providers are bound by contractual confidentiality and
        data-processing obligations.
      </p>

      <h2>5. Data Storage and International Transfer</h2>
      <p>
        Your data is stored on servers located in India, the United States, and the
        European Union, depending on the service provider. By using the Service, you
        consent to the transfer of your data to these locations. We ensure appropriate
        safeguards are in place for any cross-border transfer.
      </p>

      <h2>6. Data Retention</h2>
      <ul>
        <li>Account data — retained while your account is active, and for 12 months after closure for legal and accounting purposes.</li>
        <li>Voice recordings — retained for 24 months from your most recent scan, or until you request deletion.</li>
        <li>Reports and analysis — retained while your account is active.</li>
        <li>Payment records — retained as required by Indian tax and accounting law (currently 8 years).</li>
      </ul>

      <h2>7. Your Rights</h2>
      <p>Under India's Digital Personal Data Protection Act, 2023, you have the right to:</p>
      <ul>
        <li>Access — request a copy of the personal data we hold about you.</li>
        <li>Correction — request correction of inaccurate data.</li>
        <li>Erasure — request deletion of your data, subject to legal retention requirements.</li>
        <li>Withdraw consent — at any time, by contacting us. This will end your ability to use the Service.</li>
        <li>Grievance redressal — lodge a complaint with our Grievance Officer.</li>
      </ul>
      <p>
        To exercise any of these rights, email{" "}
        <a href="mailto:admin@voicematrix.in">admin@voicematrix.in</a>.
      </p>

      <h2>8. Cookies and Tracking</h2>
      <p>
        We use cookies and similar technologies for authentication, session management,
        and analytics. You can disable cookies in your browser, but parts of the Service
        may stop functioning.
      </p>

      <h2>9. Children's Privacy</h2>
      <p>
        The Service is intended for users aged 18 and above. We do not knowingly collect
        data from minors. If you believe a minor has submitted data, contact us and we
        will delete it.
      </p>

      <h2>10. Security</h2>
      <p>
        We implement industry-standard safeguards: encrypted transmission (HTTPS),
        encrypted storage, access controls, and periodic security reviews. No system is
        completely secure; you use the Service at your own risk.
      </p>

      <h2>11. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy. Material changes will be notified by email or
        by prominent notice on the site. Continued use after the effective date
        constitutes acceptance.
      </p>

      <h2>12. Grievance Officer</h2>
      <p>For any privacy-related concern:</p>
      <ul>
        <li><strong>Grievance Officer:</strong> Dr. Mahesh Hukmani</li>
        <li><strong>Email:</strong> <a href="mailto:admin@voicematrix.in">admin@voicematrix.in</a></li>
        <li><strong>Address:</strong> Chandigarh, U.T., India</li>
      </ul>
      <p>We will respond within 30 days.</p>
    </LegalPage>
  );
}